import React, { useState, useEffect } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Dimensions,
  Platform,
  TouchableWithoutFeedback,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import useTheme from '../../hooks/useTheme';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

export type AlertType = 'success' | 'error' | 'warning' | 'info' | 'confirm' | 'custom';

export interface AlertButton {
  text: string;
  onPress?: () => void | Promise<void>;
  style?: 'default' | 'cancel' | 'destructive';
  isPreferred?: boolean;
}

export interface AlertOptions {
  type?: AlertType;
  icon?: string;
  iconColor?: string;
  buttons?: AlertButton[];
  dismissable?: boolean;
  customView?: React.ReactNode;
}

interface AlertData {
  title: string;
  message?: string;
  options?: AlertOptions;
}

class AlertManager {
  private static instance: AlertManager;
  private showAlert?: (data: AlertData) => void;

  static getInstance(): AlertManager {
    if (!AlertManager.instance) {
      AlertManager.instance = new AlertManager();
    }
    return AlertManager.instance;
  }

  setShowAlert(showAlert: (data: AlertData) => void) {
    this.showAlert = showAlert;
  }

  alert(title: string, message?: string, options?: AlertOptions) {
    if (this.showAlert) {
      this.showAlert({ title, message, options });
    }
  }
}

export const CustomAlert = {
  alert: (title: string, message?: string, options?: AlertOptions) => {
    AlertManager.getInstance().alert(title, message, options);
  },
  
  success: (title: string, message?: string, onPress?: () => void) => {
    AlertManager.getInstance().alert(title, message, {
      type: 'success',
      buttons: [{ text: 'OK', onPress, style: 'default' }],
    });
  },
  
  error: (title: string, message?: string, onPress?: () => void) => {
    AlertManager.getInstance().alert(title, message, {
      type: 'error',
      buttons: [{ text: 'OK', onPress, style: 'default' }],
    });
  },
  
  warning: (title: string, message?: string, onPress?: () => void) => {
    AlertManager.getInstance().alert(title, message, {
      type: 'warning',
      buttons: [{ text: 'OK', onPress, style: 'default' }],
    });
  },
  
  info: (title: string, message?: string, onPress?: () => void) => {
    AlertManager.getInstance().alert(title, message, {
      type: 'info',
      buttons: [{ text: 'OK', onPress, style: 'default' }],
    });
  },
  
  confirm: (
    title: string,
    message?: string,
    onConfirm?: () => void | Promise<void>,
    onCancel?: () => void,
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel'
  ) => {
    AlertManager.getInstance().alert(title, message, {
      type: 'confirm',
      buttons: [
        { text: cancelText, onPress: onCancel, style: 'cancel' },
        { text: confirmText, onPress: onConfirm, style: 'destructive' },
      ],
    });
  },
};

export const CustomAlertProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { colors, isDark } = useTheme();
  const [alertData, setAlertData] = useState<AlertData | null>(null);
  const [visible, setVisible] = useState(false);
  const fadeAnim = useState(new Animated.Value(0))[0];
  const scaleAnim = useState(new Animated.Value(0.8))[0];

  useEffect(() => {
    AlertManager.getInstance().setShowAlert((data: AlertData) => {
      setAlertData(data);
      setVisible(true);
    });
  }, []);

  useEffect(() => {
    if (visible) {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 1,
          duration: 200,
          useNativeDriver: true,
        }),
        Animated.spring(scaleAnim, {
          toValue: 1,
          friction: 8,
          tension: 40,
          useNativeDriver: true,
        }),
      ]).start();
    } else {
      Animated.parallel([
        Animated.timing(fadeAnim, {
          toValue: 0,
          duration: 150,
          useNativeDriver: true,
        }),
        Animated.timing(scaleAnim, {
          toValue: 0.8,
          duration: 150,
          useNativeDriver: true,
        }),
      ]).start();
    }
  }, [visible]);

  const handleClose = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 0.8,
        duration: 150,
        useNativeDriver: true,
      }),
    ]).start(() => {
      setVisible(false);
      setAlertData(null);
    });
  };

  const handleButtonPress = async (button: AlertButton) => {
    if (button.onPress) {
      await button.onPress();
    }
    handleClose();
  };

  const getIconForType = (type?: AlertType): { name: string; color: string } => {
    switch (type) {
      case 'success':
        return { name: 'checkmark-circle', color: '#10B981' };
      case 'error':
        return { name: 'close-circle', color: '#EF4444' };
      case 'warning':
        return { name: 'warning', color: '#F59E0B' };
      case 'info':
        return { name: 'information-circle', color: colors.primary };
      case 'confirm':
        return { name: 'help-circle', color: colors.primary };
      default:
        return { name: 'information-circle', color: colors.primary };
    }
  };

  const getButtonStyle = (style?: string) => {
    switch (style) {
      case 'cancel':
        return {
          backgroundColor: colors.surface,
          borderColor: colors.border,
          borderWidth: 1,
        };
      case 'destructive':
        return {
          backgroundColor: '#EF4444',
        };
      default:
        return {
          backgroundColor: colors.primary,
        };
    }
  };

  const getButtonTextStyle = (style?: string) => {
    switch (style) {
      case 'cancel':
        return { color: colors.text };
      default:
        return { color: 'white' };
    }
  };

  if (!alertData || !visible) {
    return <>{children}</>;
  }

  const { type, icon, iconColor, buttons = [{ text: 'OK', style: 'default' }], dismissable = false, customView } = alertData.options || {};
  const iconInfo = icon ? { name: icon, color: iconColor || colors.primary } : getIconForType(type);

  return (
    <>
      {children}
      <Modal
        transparent
        visible={visible}
        animationType="none"
        onRequestClose={dismissable ? handleClose : undefined}
        statusBarTranslucent
      >
        <TouchableWithoutFeedback onPress={dismissable ? handleClose : undefined}>
          <View style={styles.overlay}>
            {Platform.OS === 'ios' && (
              <BlurView intensity={20} tint={isDark ? 'dark' : 'light'} style={StyleSheet.absoluteFillObject} />
            )}
            {Platform.OS !== 'ios' && (
              <Animated.View
                style={[
                  StyleSheet.absoluteFillObject,
                  {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    opacity: fadeAnim,
                  },
                ]}
              />
            )}
            <TouchableWithoutFeedback>
              <Animated.View
                style={[
                  styles.alertContainer,
                  {
                    backgroundColor: colors.surface,
                    opacity: fadeAnim,
                    transform: [{ scale: scaleAnim }],
                    shadowColor: colors.text,
                  },
                ]}
              >
                {/* Icon */}
                <View style={[styles.iconContainer, { backgroundColor: colors.background }]}>
                  <Ionicons name={iconInfo.name as any} size={48} color={iconInfo.color} />
                </View>

                {/* Title */}
                <Text style={[styles.title, { color: colors.text }]}>{alertData.title}</Text>

                {/* Message */}
                {alertData.message && (
                  <Text style={[styles.message, { color: colors.textSecondary }]}>{alertData.message}</Text>
                )}

                {/* Custom View */}
                {customView}

                {/* Buttons */}
                <View style={styles.buttonContainer}>
                  {buttons.map((button, index) => (
                    <TouchableOpacity
                      key={index}
                      style={[
                        styles.button,
                        getButtonStyle(button.style),
                        buttons.length > 1 && index === 0 && styles.firstButton,
                      ]}
                      onPress={() => handleButtonPress(button)}
                      activeOpacity={0.8}
                    >
                      <Text style={[styles.buttonText, getButtonTextStyle(button.style)]}>
                        {button.text}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </Animated.View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 24,
  },
  alertContainer: {
    width: '100%',
    maxWidth: 340,
    borderRadius: 20,
    padding: 24,
    alignItems: 'center',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 5,
  },
  iconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  message: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 24,
    lineHeight: 22,
  },
  buttonContainer: {
    width: '100%',
    gap: 12,
  },
  button: {
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  firstButton: {
    marginBottom: 0,
  },
  buttonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});